import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { toast } from "sonner";
import { Plus, X, Link2, AlertTriangle, RotateCcw, GitCompare } from "lucide-react";

const ALLERGENS = ["Glutine", "Crostacei", "Uova", "Pesce", "Arachidi", "Soia", "Latte", "Frutta a guscio", "Sedano", "Senape", "Sesamo", "Solfiti", "Lupini", "Molluschi"];
const CONSERV = { "": "—", frigo: "Frigorifero (0–4°C)", ambiente: "Temperatura ambiente", congelatore: "Congelatore (−18°C)", abbattitore: "Abbattuto e conservato" };
const UNITS = ["ml", "l", "g", "kg", "pz"];
const emptyIng = () => ({ name: "", ingredient_id: "", quantity: "", unit: "ml" });

export function RecipeEditor({ open, onOpenChange, initial, context = {}, onSaved }) {
  const { mode = "new", recipeId = null, standardIngredients = [] } = context;
  const [form, setForm] = useState(null);
  const [allIng, setAllIng] = useState([]);
  const [fc, setFc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null); // {type, existingId, canUpdate}
  const [reason, setReason] = useState("");
  const debRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/fc/ingredients?active=true").then(({ data }) => setAllIng(data)).catch(() => {});
    const base = initial || {};
    setForm({
      name: base.name || "", portions: base.portions || 1, selling_price: base.selling_price || 0,
      vat_rate: base.vat_rate != null ? base.vat_rate : 10,
      ingredients: (base.ingredients && base.ingredients.length)
        ? base.ingredients.map((i) => ({ name: i.name || "", ingredient_id: i.ingredient_id || "", quantity: i.quantity ?? "", unit: i.unit || "ml" }))
        : [emptyIng()],
      procedure: base.procedure || "", allergens: [...(base.allergens || [])],
      conservation: base.conservation || "", conservation_temp: base.conservation_temp || "",
      shelf_life_days: base.shelf_life_days ?? "",
      shelf_life_hours: base.shelf_life_hours ?? "", haccp_notes: base.haccp_notes || "",
      source_type: base.source_type || "manual", standard_recipe_id: base.standard_recipe_id || "",
      standard_recipe_version: base.standard_recipe_version || null, sheet_id: base.sheet_id || "", dish_id: base.dish_id || "",
    });
    setConflict(null); setReason(""); setFc(null);
  }, [open, initial]);

  const recompute = useCallback((f) => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      const ings = f.ingredients.filter((i) => i.ingredient_id || i.name).map((i) => ({ ingredient_id: i.ingredient_id, name: i.name, quantity: parseFloat(i.quantity) || 0, unit: i.unit }));
      try {
        const { data } = await api.post("/fc/recipes/preview", { portions: parseFloat(f.portions) || 1, selling_price: parseFloat(f.selling_price) || 0, vat_rate: parseFloat(f.vat_rate), ingredients: ings });
        setFc(data);
      } catch { setFc(null); }
    }, 350);
  }, []);
  useEffect(() => { if (form) recompute(form); }, [form, recompute]);

  if (!form) return null;
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setIng = (i, k, v) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const pickMaster = (i, m) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((it, idx) => idx === i ? { ...it, ingredient_id: m.id, name: it.name || m.canonical_name, unit: m.base_unit || it.unit } : it) }));
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, emptyIng()] }));
  const delIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));
  const toggleAll = (a) => setForm((f) => ({ ...f, allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a] }));

  const restoreStandard = () => {
    if (!standardIngredients.length) return;
    if (!window.confirm("Ripristinare le dosi standard? Le dosi operative correnti verranno sovrascritte.")) return;
    setForm((f) => ({ ...f, ingredients: standardIngredients.map((s) => {
      const cur = f.ingredients.find((x) => (x.name || "").toLowerCase() === (s.name || "").toLowerCase());
      return { name: s.name, ingredient_id: cur?.ingredient_id || s.ingredient_id || "", quantity: s.qty ?? s.quantity ?? "", unit: s.unit || "ml" };
    }) }));
    toast.message("Dosi standard ripristinate (non ancora salvate)");
  };

  const payload = (force = false) => ({
    name: form.name.trim(), portions: parseFloat(form.portions) || 1, selling_price: parseFloat(form.selling_price) || 0,
    vat_rate: parseFloat(form.vat_rate), ingredients: form.ingredients.filter((i) => i.name || i.ingredient_id).map((i) => ({ name: i.name, ingredient_id: i.ingredient_id, quantity: parseFloat(i.quantity) || 0, unit: i.unit })),
    procedure: form.procedure, allergens: form.allergens, conservation: form.conservation, conservation_temp: form.conservation_temp,
    shelf_life_days: form.shelf_life_days === "" ? null : parseInt(form.shelf_life_days),
    shelf_life_hours: form.shelf_life_hours === "" ? null : parseInt(form.shelf_life_hours),
    haccp_notes: form.haccp_notes || "",
    source_type: form.source_type, standard_recipe_id: form.standard_recipe_id, standard_recipe_version: form.standard_recipe_version,
    customized: form.source_type === "standard_customized", sheet_id: form.sheet_id, dish_id: form.dish_id,
    change_reason: reason, force,
  });

  const done = (msg) => { toast.success(msg); onOpenChange(false); onSaved && onSaved(); };

  const doSave = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome prodotto");
    if (form.shelf_life_hours !== "" && (isNaN(parseInt(form.shelf_life_hours)) || parseInt(form.shelf_life_hours) < 0)) return toast.error("Shelf-life (ore) non valida: inserisci un valore ≥ 0");
    setSaving(true);
    try {
      if (mode === "edit" && recipeId) {
        const { data: ce } = await api.get(`/recipes/${recipeId}/can-edit`);
        setConflict({ type: "edit", existingId: recipeId, canUpdate: ce.can_update_in_place });
        setSaving(false); return;
      }
      await api.post("/recipes/save", payload(false));
      done("Ricetta creata");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "RECIPE_EXISTS") {
        setConflict({ type: "exists", existingId: d.existing_id, canUpdate: false });
      } else toast.error(formatApiError(d));
    } finally { setSaving(false); }
  };

  const updateInPlace = async () => {
    setSaving(true);
    try { await api.put(`/recipes/${conflict.existingId}`, payload(false)); done("Versione attiva aggiornata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const createVersion = async () => {
    setSaving(true);
    try { await api.post(`/recipes/${conflict.existingId}/new-version`, payload(false)); done("Nuova versione creata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const openExisting = async () => {
    try {
      const { data } = await api.get(`/fc/recipes/${conflict.existingId}`);
      setForm((f) => ({ ...f, name: data.name, portions: data.portions, selling_price: data.selling_price, vat_rate: data.vat_rate,
        ingredients: (data.ingredients || []).map((i) => ({ name: i.name, ingredient_id: i.ingredient_id || "", quantity: i.quantity, unit: i.unit })) }));
      context.mode = "edit"; context.recipeId = conflict.existingId;
      setConflict(null); toast.message("Ricetta esistente caricata: modifica e salva");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Metric = ({ label, value, strong }) => (
    <div className="rounded-lg bg-muted/60 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`${strong ? "text-lg font-bold" : "text-sm font-semibold"}`}>{value}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="recipe-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === "edit" ? "Modifica ricetta" : "Nuova ricetta"}
            {form.source_type === "standard_customized" && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-600">Operativa Dogana (da standard)</span>}
            {form.source_type === "custom" && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-600">Custom</span>}
            {form.source_type === "from_sheet" && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">Da scheda prodotto</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2 space-y-1"><Label>Nome prodotto *</Label><Input value={form.name} onChange={(e) => upd({ name: e.target.value })} data-testid="recipe-name" /></div>
            <div className="space-y-1"><Label>Porzioni</Label><Input type="number" min="1" value={form.portions} onChange={(e) => upd({ portions: e.target.value })} data-testid="recipe-portions" /></div>
            <div className="space-y-1"><Label>Prezzo vendita €</Label><Input type="number" step="0.01" value={form.selling_price} onChange={(e) => upd({ selling_price: e.target.value })} data-testid="recipe-price" /></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label>Ingredienti &amp; dosi</Label>
              <div className="flex gap-2">
                {standardIngredients.length > 0 && <Button type="button" size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={restoreStandard} data-testid="recipe-restore-standard"><RotateCcw className="w-3.5 h-3.5 mr-1" />Ripristina dosi standard</Button>}
                <Button type="button" size="sm" variant="outline" className="rounded-full h-8" onClick={addIng} data-testid="recipe-add-ing"><Plus className="w-3.5 h-3.5 mr-1" />Riga</Button>
              </div>
            </div>
            {form.ingredients.map((it, i) => {
              const row = fc?.ingredients?.[i];
              const missing = row && row.missing_price;
              return (
                <div key={i} className="rounded-xl border border-border p-2.5 grid grid-cols-2 sm:grid-cols-12 gap-2 items-end" data-testid={`recipe-ing-${i}`}>
                  <div className="col-span-2 sm:col-span-4 space-y-1">
                    <Label className="text-xs text-muted-foreground">Nome</Label>
                    <Input value={it.name} onChange={(e) => setIng(i, "name", e.target.value)} className="h-9" data-testid={`recipe-ing-name-${i}`} />
                  </div>
                  <div className="col-span-2 sm:col-span-4 space-y-1">
                    <Label className="text-xs text-muted-foreground">Master Ingredient</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="h-9 w-full justify-start font-normal text-xs" data-testid={`recipe-ing-master-${i}`}>
                          <Link2 className="w-3.5 h-3.5 mr-1 shrink-0" />{it.ingredient_id ? (allIng.find((m) => m.id === it.ingredient_id)?.canonical_name || "Collegato") : "Seleziona master"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[300px]" align="start">
                        <Command>
                          <CommandInput placeholder="Cerca master…" />
                          <CommandList>
                            <CommandEmpty>Nessun master</CommandEmpty>
                            <CommandGroup>
                              {allIng.slice(0, 500).map((m) => (
                                <CommandItem key={m.id} value={m.canonical_name} onSelect={() => pickMaster(i, m)} data-testid={`recipe-master-opt-${m.id}`}>
                                  <span className="text-sm">{m.canonical_name}</span><span className="ml-2 text-xs text-muted-foreground">{m.base_unit}</span>{m.is_semi_finished && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-600 font-semibold">semilavorato</span>}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="col-span-1 sm:col-span-2 space-y-1"><Label className="text-xs text-muted-foreground">Q.tà</Label><Input type="number" step="0.01" value={it.quantity} onChange={(e) => setIng(i, "quantity", e.target.value)} className="h-9" data-testid={`recipe-ing-qty-${i}`} /></div>
                  <div className="col-span-1 sm:col-span-2 space-y-1">
                    <Label className="text-xs text-muted-foreground">Unità</Label>
                    <div className="flex items-center gap-1">
                      <Select value={it.unit} onValueChange={(v) => setIng(i, "unit", v)}><SelectTrigger className="h-9" data-testid={`recipe-ing-unit-${i}`}><SelectValue /></SelectTrigger><SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
                      <button type="button" onClick={() => delIng(i)} className="shrink-0 text-destructive p-1" data-testid={`recipe-ing-del-${i}`}><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {missing && <p className="col-span-2 sm:col-span-12 text-xs text-amber-600">⚠ COSTO NON DISPONIBILE per questo master — nessun prezzo reale in archivio.</p>}
                </div>
              );
            })}
          </div>

          {/* FOOD COST LIVE */}
          <div className="rounded-xl border border-border p-3" data-testid="recipe-fc-live">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Food Cost live</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Metric label="Costo/porzione" value={fc?.cost_per_portion != null ? `€ ${fc.cost_per_portion.toFixed(2)}` : "—"} />
              <Metric label="Prezzo netto" value={fc?.net_selling_price != null ? `€ ${fc.net_selling_price.toFixed(2)}` : "—"} />
              <Metric label="Food Cost netto" value={fc?.food_cost_pct_net != null ? `${fc.food_cost_pct_net}%` : "—"} strong />
              <Metric label="Margine netto" value={fc?.margin_net != null ? `€ ${fc.margin_net.toFixed(2)}` : "—"} />
              <Metric label="Margine %" value={fc?.margin_pct_net != null ? `${fc.margin_pct_net}%` : "—"} />
            </div>
            {fc?.incomplete && <p className="text-xs text-amber-600 mt-2">Ricetta incompleta: alcuni ingredienti non hanno costo reale.</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1"><Label>Conservazione</Label>
              <Select value={form.conservation || "none"} onValueChange={(v) => upd({ conservation: v === "none" ? "" : v })}><SelectTrigger data-testid="recipe-conservation"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CONSERV).map(([k, v]) => <SelectItem key={k || "none"} value={k || "none"}>{v}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1"><Label>Temperatura</Label><Input value={form.conservation_temp} onChange={(e) => upd({ conservation_temp: e.target.value })} data-testid="recipe-temp" /></div>
            <div className="space-y-1"><Label>Shelf-life (giorni)</Label><Input type="number" min="0" value={form.shelf_life_days} onChange={(e) => upd({ shelf_life_days: e.target.value })} placeholder="opzionale" data-testid="recipe-shelf" /></div>
            <div className="space-y-1"><Label>Shelf-life (ore)</Label><Input type="number" min="0" value={form.shelf_life_hours} onChange={(e) => upd({ shelf_life_hours: e.target.value })} placeholder="opzionale" data-testid="recipe-shelf-hours" /></div>
          </div>

          <div className="space-y-1"><Label>Note HACCP</Label>
            <textarea value={form.haccp_notes} onChange={(e) => upd({ haccp_notes: e.target.value })} rows={2} placeholder="opzionale — annotazioni HACCP (es. raffreddamento rapido, conservazione sottovuoto…)" data-testid="recipe-haccp-notes"
              className="flex w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          </div>

          <div className="space-y-1">
            <Label>Allergeni</Label>
            <div className="flex flex-wrap gap-2">
              {ALLERGENS.map((a) => (
                <button key={a} type="button" onClick={() => toggleAll(a)} data-testid={`recipe-allergen-${a}`}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${form.allergens.includes(a) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-transparent hover:border-primary/40"}`}>{a}</button>
              ))}
            </div>
          </div>

          <div className="space-y-1"><Label>Procedura di preparazione</Label>
            <textarea value={form.procedure} onChange={(e) => upd({ procedure: e.target.value })} rows={3} data-testid="recipe-procedure"
              className="flex w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={doSave} disabled={saving} className="rounded-full" data-testid="recipe-save-btn">{saving ? "Salvataggio…" : "Salva ricetta"}</Button>
        </DialogFooter>

        {/* CONFLITTO / VERSIONING */}
        {conflict && (
          <Dialog open={true} onOpenChange={() => setConflict(null)}>
            <DialogContent className="max-w-md" data-testid="recipe-conflict-dialog">
              <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><AlertTriangle className="w-5 h-5 text-amber-500" />{conflict.type === "exists" ? "Ricetta già esistente" : "Salvataggio modifica"}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">
                {conflict.type === "exists"
                  ? "Esiste già una ricetta attiva per questo prodotto. Il nome commerciale resta invariato."
                  : conflict.canUpdate
                    ? "Puoi aggiornare la versione attiva oppure creare una nuova versione (la precedente resta nello storico)."
                    : "Esistono produzioni definitive con questa versione: per non alterare lo storico HACCP devi creare una NUOVA VERSIONE."}
              </p>
              <div className="space-y-2">
                <Label className="text-xs">Motivo modifica (opzionale)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="es. nuova dose, cambio marca…" data-testid="recipe-change-reason" />
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {conflict.type === "exists" && <Button variant="outline" className="rounded-full" onClick={openExisting} data-testid="conflict-open-existing">Apri esistente</Button>}
                {conflict.type === "edit" && conflict.canUpdate && <Button variant="outline" className="rounded-full" onClick={updateInPlace} disabled={saving} data-testid="conflict-update">Aggiorna versione attiva</Button>}
                <Button className="rounded-full" onClick={createVersion} disabled={saving} data-testid="conflict-new-version"><GitCompare className="w-4 h-4 mr-1" />Crea nuova versione</Button>
                <Button variant="ghost" className="rounded-full" onClick={() => setConflict(null)} data-testid="conflict-cancel">Annulla</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
